import { useEffect, useRef } from 'react';
import { callRefresh } from '../api/refresh';
import { useAppStore } from '../state/store';

/**
 * When the user has instruments but any of them lack OHLC data (e.g. just added
 * primary or an additional symbol, no backtest run), requests OHLC from the
 * refresh API and merges the response into the store so charts can display
 * without running a backtest.
 */
export function useFetchOhlcWhenNeeded() {
  const primaryInstrumentId = useAppStore((s) => s.primaryInstrumentId);
  const instruments = useAppStore((s) => s.instruments);
  const ohlc = useAppStore((s) => s.ohlc);
  const setOhlcLoading = useAppStore((s) => s.setOhlcLoading);
  const setOhlcError = useAppStore((s) => s.setOhlcError);
  const loadFromRefreshResponse = useAppStore((s) => s.loadFromRefreshResponse);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!primaryInstrumentId || instruments.length === 0) return;

    const hasOhlcForAll = instruments.every(
      (inst) =>
        ohlc?.[inst.id] &&
        Object.keys(ohlc[inst.id].series ?? {}).length > 0
    );
    if (hasOhlcForAll) return;

    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setOhlcLoading(true);
    setOhlcError(null);

    const state = useAppStore.getState();
    const request = {
      refresh: 'ohlc' as const,
      instruments: state.instruments,
      primaryInstrumentId: state.primaryInstrumentId!,
      indicators: state.indicators,
    };

    callRefresh(request)
      .then((response) => {
        loadFromRefreshResponse(response);
      })
      .catch((err) => {
        console.error('OHLC refresh failed:', err);
        const message = err instanceof Error ? err.message : String(err);
        useAppStore.getState().setOhlcError(message);
      })
      .finally(() => {
        inFlightRef.current = false;
        useAppStore.getState().setOhlcLoading(false);
      });
  }, [primaryInstrumentId, instruments, ohlc, setOhlcLoading, setOhlcError, loadFromRefreshResponse]);
}
